import { Beaker, Layers3, ShieldCheck, Wheat, PackageCheck, FileCheck2, Container, Ship, ScanLine, Sprout } from "lucide-react";
import Reveal from "../components/Reveal";
import PageHeader from "../components/PageHeader";

const STEPS = [
  { icon: Sprout, title: "Premium Raw Material Selection", body: "Direct sourcing from region-specific cooperatives — Malabar pepper, Guntur chilli, Cochin ginger — chosen by cultivar, colour and season." },
  { icon: Wheat, title: "Cleaning", body: "Multi-stage mechanical cleaning removes soil, dust, stems and foreign matter before any further processing begins." },
  { icon: ScanLine, title: "Sorting", body: "Sortex colour sorting and gravity separation deliver visual uniformity and remove defective grains and stones." },
  { icon: Layers3, title: "Grinding", body: "Temperature-controlled cold grinding preserves volatile oils and aroma compounds — critical for premium ground spices." },
  { icon: Beaker, title: "Laboratory Testing", body: "Every lot is tested for moisture, colour value (ASTA), essential oil content, microbial load and heavy-metal thresholds." },
  { icon: ShieldCheck, title: "Quality Inspection", body: "Final QC pass verifies specifications against buyer PO — mesh size, moisture, sensory profile and appearance." },
  { icon: PackageCheck, title: "Packaging", body: "Food-grade PP bags with liners, nitrogen-flushed pouches, PET jars or custom private-label formats per order specification." },
  { icon: FileCheck2, title: "Export Documentation", body: "Commercial Invoice, Packing List, Phytosanitary Certificate, FSSAI Health Certificate and Certificate of Analysis prepared." },
  { icon: Container, title: "Container Loading", body: "Palletized or floor-loaded containers, moisture-control desiccants, and photo-documented loading for buyer records." },
  { icon: Ship, title: "Worldwide Dispatch", body: "Trusted freight forwarders across major shipping lines — timely departure, tracking updates and clean arrival documentation." },
];

export default function Quality() {
  return (
    <>
      <PageHeader
        eyebrow="Quality Assurance"
        title="From field to container, a ten-step discipline."
        italicWord="ten-step"
        subtitle="Consistency is what builds international trust. Every export lot passes through the same protocol — no shortcuts, no exceptions, no surprises at destination."
      />

      {/* TIMELINE */}
      <section className="py-24 md:py-32 bg-brand-ivory">
        <div className="container-luxe">
          <div className="grid lg:grid-cols-2 gap-x-16 gap-y-16">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 40} className="relative pl-16">
                <div className="absolute left-0 top-0 flex flex-col items-center">
                  <div className="w-12 h-12 border border-brand-gold flex items-center justify-center text-brand-gold bg-brand-ivory">
                    <s.icon className="w-5 h-5" />
                  </div>
                  <div className="mt-3 text-[10px] tracking-[0.3em] font-semibold text-brand-charcoal/40">{String(i + 1).padStart(2, "0")}</div>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-brand-charcoal mb-3">{s.title}</h3>
                <p className="text-brand-charcoal/65 text-[15px] leading-relaxed">{s.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* METRICS */}
      <section className="py-24 bg-brand-ink text-brand-ivory">
        <div className="container-luxe">
          <Reveal className="max-w-xl mb-14">
            <span className="overline">Testing Parameters</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">What we measure, every lot.</h2>
            <span className="divider-gold" />
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { k: "Moisture %", v: "Below product-specific threshold" },
              { k: "Colour (ASTA)", v: "Verified for chilli, turmeric, paprika" },
              { k: "Essential Oil", v: "Volatile oil retention profile" },
              { k: "Foreign Matter", v: "Below 1% by weight" },
              { k: "Aflatoxin", v: "Within EU / codex limits" },
              { k: "Pesticide Residue", v: "MRL compliance per destination" },
              { k: "Microbial Load", v: "TPC, E. coli, Salmonella" },
              { k: "Heavy Metals", v: "Lead, Arsenic, Cadmium checked" },
            ].map((m) => (
              <div key={m.k} className="border-l border-brand-gold/40 pl-5">
                <div className="font-serif text-2xl text-brand-gold mb-2">{m.k}</div>
                <div className="text-brand-ivory/60 text-[13.5px] leading-relaxed">{m.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
